/* ============================================================================
   domain_selector — domain ⇄ builder-tree (de)serialization.

   serializeDomain(group)  : builder tree  → EDE domain (prefix notation)
   parseDomain(domain)     : EDE domain    → builder tree

   Round-trip stable for the shapes the visual builder produces:
     · implicit-AND lists + `&` / `|` nesting
     · `set` / `unset`  (→ [f,"!=",false] / [f,"=",false])
     · date/datetime `between`  (→ "&", [f,">=",from], [f,"<=",to])
     · `in` / `not in` array values
     · `$principal.*` / `$today` tokens preserved verbatim
   `!` negating a single leaf is folded into the leaf's operator; `!` over a
   group throws DomainParseError so the caller can fall back to the Code tab.
   ========================================================================== */
import type { Condition, Domain, DomainLeaf, DomainScalar, Group, Node, RangeValue } from "./types";
import { DomainParseError, isRange } from "./types";

/* ---- tree → domain ---------------------------------------------------- */

function conditionTokens(c: Condition): Domain {
    if (c.op === "set") return [[c.field, "!=", false]];
    if (c.op === "unset") return [[c.field, "=", false]];
    if (c.op === "between") {
        const r = c.value as RangeValue;
        return ["&", [c.field, ">=", r.from], [c.field, "<=", r.to]];
    }
    return [[c.field, c.op, c.value as DomainScalar]];
}

function nodeTokens(node: Node): Domain {
    if (node.kind === "cond") return conditionTokens(node);
    const parts = node.items.map(nodeTokens);
    if (node.join === "AND") return parts.flat();
    // OR of N operands → (N-1) "|" prefixes then the operands' tokens.
    const ors = Math.max(0, parts.length - 1);
    return [...Array<DomainTerm>(ors).fill("|"), ...parts.flat()];
}
type DomainTerm = Domain[number];

export function serializeDomain(root: Group): Domain {
    return nodeTokens(root);
}

/* ---- domain → tree ---------------------------------------------------- */

const ARITY: Record<string, number> = { "!": 1, "&": 2, "|": 2 };

/** Odoo-style normalization: make the implicit-AND domain fully prefixed. */
export function normalizeDomain(domain: Domain): Domain {
    if (!domain.length) return [];
    const result: Domain = [];
    let expected = 1;
    for (const token of domain) {
        if (expected === 0) {
            result.unshift("&");
            expected = 1;
        }
        result.push(token);
        if (Array.isArray(token)) expected -= 1;
        else expected += (ARITY[token] ?? 0) - 1;
    }
    return result;
}

const FLIP: Record<string, string> = {
    "=": "!=", "!=": "=", ">": "<=", "<=": ">", "<": ">=", ">=": "<",
    in: "not in", "not in": "in", ilike: "not ilike", "not ilike": "ilike",
    like: "not like", "not like": "like",
};

function leafToCondition(leaf: DomainLeaf): Condition {
    const [field, op, value] = leaf;
    if (op === "!=" && value === false) return { kind: "cond", field, op: "set", value: null };
    if (op === "=" && value === false) return { kind: "cond", field, op: "unset", value: null };
    const dyn = typeof value === "string" && value.startsWith("$");
    return { kind: "cond", field, op, value, ...(dyn ? { dyn: true } : {}) };
}

function negate(node: Node): Node {
    if (node.kind === "group") throw new DomainParseError("`!` over a group is not representable in the builder");
    if (!(node.op in FLIP)) throw new DomainParseError(`cannot negate operator "${node.op}"`);
    return { ...node, op: FLIP[node.op] };
}

function mergeGroup(join: "AND" | "OR", a: Node, b: Node): Group {
    const items: Node[] = [];
    for (const n of [a, b]) {
        if (n.kind === "group" && n.join === join) items.push(...n.items);
        else items.push(n);
    }
    return { kind: "group", join, items };
}

/** Collapse an AND group's [f,">=",a] + [f,"<=",b] pair into one `between`. */
function collapseBetween(group: Group): Group {
    const items = group.items.map((n) => (n.kind === "group" ? collapseBetween(n) : n));
    if (group.join !== "AND") return { ...group, items };
    const out: Node[] = [];
    const used = new Set<number>();
    for (let i = 0; i < items.length; i++) {
        if (used.has(i)) continue;
        const a = items[i];
        if (a.kind === "cond" && a.op === ">=" && !a.dyn) {
            const j = items.findIndex(
                (n, k) => k > i && !used.has(k) && n.kind === "cond" && n.op === "<=" && n.field === a.field && !n.dyn,
            );
            if (j >= 0) {
                const b = items[j] as Condition;
                used.add(i); used.add(j);
                out.push({ kind: "cond", field: a.field, op: "between", value: { from: String(a.value), to: String(b.value) } });
                continue;
            }
        }
        out.push(a);
    }
    return { ...group, items: out };
}

export function parseDomain(domain: Domain): Group {
    const norm = normalizeDomain(domain);
    if (!norm.length) return { kind: "group", join: "AND", items: [] };
    let i = 0;
    const next = (): Node => {
        const t = norm[i++];
        if (t === undefined) throw new DomainParseError("unexpected end of domain");
        if (Array.isArray(t)) return leafToCondition(t);
        if (t === "&" || t === "|") return mergeGroup(t === "&" ? "AND" : "OR", next(), next());
        if (t === "!") return negate(next());
        throw new DomainParseError(`unexpected token: ${JSON.stringify(t)}`);
    };
    const node = next();
    if (i !== norm.length) throw new DomainParseError("trailing tokens after parse");
    const root: Group = node.kind === "group" ? node : { kind: "group", join: "AND", items: [node] };
    return collapseBetween(root);
}

/* ---- readable summary (used by the widget read-state) ----------------- */

export function conditionCount(node: Node): number {
    return node.kind === "cond" ? 1 : node.items.reduce((n, c) => n + conditionCount(c), 0);
}

export { isRange };
